// The eleven field types, transcribed from 09-field-library.md section 3.
//
// The list is closed and this file is the closure. A client assembles a field
// set from these types and configures labels, options, bounds and order; a
// client cannot add a type, write a rule or lay out a page. Section 1 of that
// document states why in one line: a form builder produces data whose shape
// nobody predicted, which means no standard report can read it.
//
// Written declaratively so that a change to the library is a change to a
// literal here rather than to logic somewhere else, on the same reasoning the
// frozen schema package gives for its own event list.

export const FIELD_TYPES = [
  "text",
  "long_text",
  "number",
  "date",
  "email",
  "phone",
  "single_select",
  "multi_select",
  "boolean",
  "status",
  "survey_question",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/** The closed set of answer formats a survey question may take. */
export const ANSWER_FORMATS = ["single_choice", "multi_choice", "rating_scale", "free_text"] as const;

export type AnswerFormat = (typeof ANSWER_FORMATS)[number];

/**
 * Nova's ceilings. A client sets bounds within these and never above them.
 *
 * All are design targets pending the confirmation asked for in section 12 of
 * the library document. `long_text` is the one that matters for anything other
 * than tidiness: section 8 names it as the only type whose ceiling can make
 * events a real share of device storage at a busy show.
 */
export const CEILINGS = {
  text: 200,
  long_text: 2_000,
  email: 320,
  phone: 32,
  other_text: 200,
  rating_points: 10,
} as const;

/**
 * The worst-case size of one registration payload above which the
 * configuration surface warns. Section 8 asks for the warning and says the
 * threshold is a design target to be set once measured. 8 kB is one screen of
 * long text plus everything else, and it is a number to replace with a
 * measured one rather than to defend.
 */
export const PAYLOAD_WARN_BYTES = 8_192;

export type FieldOption = {
  /** Stable. Stored instead of the label, so a typo can be fixed after capture. */
  id: string;
  label: string;
};

/**
 * One level of dependency, no nesting. Section 4.
 *
 * Chains are refused by checkFieldSet rather than merely discouraged: deep
 * conditional logic is a form builder in disguise and it produces exactly the
 * unpredictable shape the library exists to prevent.
 */
export type VisibleWhen = {
  field_id: string;
  equals: string | number | boolean;
};

type Common = {
  /** Assigned at configuration, never reused, never changed after capture. */
  field_id: string;
  help_text?: string;
  required?: boolean;
  visible_when?: VisibleWhen;
};

type Labelled = Common & { label: string };

export type TextField = Labelled & {
  type: "text";
  max_length?: number;
  placeholder?: string;
};

export type LongTextField = Labelled & {
  type: "long_text";
  max_length?: number;
};

export type NumberField = Labelled & {
  type: "number";
  min?: number;
  max?: number;
  integer_only?: boolean;
  unit_label?: string;
};

export type DateField = Labelled & {
  type: "date";
  /** ISO 8601 date, no time component. */
  earliest?: string;
  latest?: string;
};

export type EmailField = Labelled & { type: "email" };

export type PhoneField = Labelled & {
  type: "phone";
  /** ISO 3166-1 alpha-2. Used for normalisation only, never to refuse a number. */
  default_country?: string;
};

export type SingleSelectField = Labelled & {
  type: "single_select";
  options: FieldOption[];
  allow_other?: boolean;
  other_label?: string;
};

export type MultiSelectField = Labelled & {
  type: "multi_select";
  options: FieldOption[];
  min_selections?: number;
  max_selections?: number;
};

export type BooleanField = Labelled & {
  type: "boolean";
  true_label: string;
  false_label: string;
};

/**
 * Crew-set, never attendee-facing. Section 3.10.
 *
 * `crew_only` is on the type rather than optional on every type, because a
 * status is the only thing in the library that a client may not put in front
 * of an attendee, and making it a flag anybody can set on anything is how it
 * ends up set on nothing.
 */
export type StatusField = Labelled & {
  type: "status";
  options: FieldOption[];
  crew_only: true;
};

/**
 * Kept apart from the attribute types on purpose. Section 3.11: "Job title"
 * describes a person and "How likely are you to specify birch ply?" describes
 * an opinion at a moment, and a report that can count one without counting the
 * other needs them to be different types.
 */
export type SurveyQuestionField = Common & {
  type: "survey_question";
  question: string;
  answer_format: AnswerFormat;
  options?: FieldOption[];
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  max_length?: number;
};

export type FieldDefinition =
  | TextField
  | LongTextField
  | NumberField
  | DateField
  | EmailField
  | PhoneField
  | SingleSelectField
  | MultiSelectField
  | BooleanField
  | StatusField
  | SurveyQuestionField;

/** A chosen "other" option and the words that went with it. */
export type OtherValue = {
  option_id: string;
  other_text: string;
};

export type FieldValue = string | number | boolean | string[] | OtherValue | null;

/** Keyed by `field_id`, which is what the registration payload carries. */
export type FieldValues = Readonly<Record<string, FieldValue>>;

export const isOtherValue = (value: FieldValue): value is OtherValue =>
  typeof value === "object" && value !== null && !Array.isArray(value) && "option_id" in value;

/** The words at the top of a field. A survey question has a question, not a label. */
export function fieldLabel(field: FieldDefinition): string {
  return field.type === "survey_question" ? field.question : field.label;
}

/** Options, where the type has any. */
export function fieldOptions(field: FieldDefinition): readonly FieldOption[] {
  switch (field.type) {
    case "single_select":
    case "multi_select":
    case "status":
      return field.options;
    case "survey_question":
      return field.options ?? [];
    default:
      return [];
  }
}
